import React, { useCallback, useEffect, useState } from "react";

import * as api from "../api";
import Pill from "../components/Pill";
import { dotColor, gateTone, pillStyle, type PillTone } from "../logic";
import type { PageProps } from "./types";

const selectStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: "var(--font-sans)",
  fontSize: 13.5,
  color: "var(--ink-900)",
  background: "#fff",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  padding: "10px 12px",
  cursor: "pointer",
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-sans)",
  fontWeight: 600,
  fontSize: 12,
  color: "var(--ink-700)",
  marginBottom: 6,
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid var(--border-hairline)",
  borderRadius: 16,
  padding: 24,
  boxShadow: "var(--elevation-sm)",
};

function StatCard({ label, value, unit, color }: { label: string; value: string; unit: string; color: string }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 14, padding: "18px 20px", boxShadow: "var(--elevation-sm)" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 30, letterSpacing: "-0.02em", color }}>{value}</span>
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{unit}</span>
      </div>
    </div>
  );
}

export default function Support({ intent }: PageProps) {
  const [summary, setSummary] = useState<api.Summary | null>(null);
  const [orgs, setOrgs] = useState<api.OrgRow[]>([]);
  const [orgId, setOrgId] = useState<string>("");
  const [members, setMembers] = useState<api.OrgMember[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [readiness, setReadiness] = useState<api.ReadinessResult | null>(null);
  const [readyErr, setReadyErr] = useState<string | null>(null);
  const [health, setHealth] = useState<api.AnalyticsHealth | null>(null);
  const [recon, setRecon] = useState<api.ReconciliationRun[]>([]);

  // Load summary, org list, analytics-health, reconciliation once.
  useEffect(() => {
    api.getSummary().then(setSummary, () => setSummary(null));
    api.searchOrgs("").then(
      (r) => {
        setOrgs(r.orgs);
        // honor an org preselect from the Orgs page; else pick the first.
        setOrgId((prev) => prev || intent?.readinessOrgId || r.orgs[0]?.id || "");
      },
      () => setOrgs([]),
    );
    api.getAnalyticsHealth().then(setHealth, () => setHealth(null));
    api.listReconciliationRuns(1).then((r) => setRecon(r.runs), () => setRecon([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When org changes, reset user and load members.
  useEffect(() => {
    if (!orgId) return;
    setUserId("");
    setMembers([]);
    api.listOrgMembers(orgId).then((r) => setMembers(r.members), () => setMembers([]));
  }, [orgId]);

  const runReadiness = useCallback(() => {
    if (!orgId) return;
    setReadyErr(null);
    api.getReadiness(orgId, userId || undefined).then(setReadiness, (e) => {
      setReadiness(null);
      setReadyErr(e?.message || "Readiness unavailable");
    });
  }, [orgId, userId]);

  // Re-evaluate whenever org/user changes.
  useEffect(() => {
    runReadiness();
  }, [runReadiness]);

  const statCards = summary
    ? [
        { label: "Organizations", value: String(summary.organizations_total), unit: "total", color: "var(--ink-900)" },
        { label: "Users", value: String(summary.users_total), unit: "total", color: "var(--ink-900)" },
        { label: "Dialing disabled", value: String(summary.dialing_disabled_count), unit: "orgs", color: summary.dialing_disabled_count ? "var(--danger-500)" : "var(--ink-900)" },
        { label: "Low balance", value: String(summary.low_balance_count), unit: "< 60 min", color: summary.low_balance_count ? "#8A5209" : "var(--ink-900)" },
      ]
    : [];

  const verdict = readiness;
  const canDial = verdict?.can_dial ?? false;

  // Platform-health rows.
  const stale = !!health?.stale;
  const lastRun = recon[0];
  const healthRows = [
    {
      label: "Analytics forwarding",
      tone: (stale ? "danger" : "ok") as PillTone,
      state: stale ? "stale" : "healthy",
      detail: stale ? "No successful forward in >24h" : "Forwarding to Mixpanel nominally",
    },
    {
      label: "Telephony reconciliation",
      tone: (lastRun && lastRun.discrepancies > 0 ? "warn" : "ok") as PillTone,
      state: lastRun ? (lastRun.discrepancies > 0 ? "attention" : "ok") : "no runs",
      detail: lastRun
        ? `${lastRun.calls_checked.toLocaleString()} checked · ${lastRun.discrepancies} discrepancies`
        : "No recent runs — reconciliation may not be running",
    },
    {
      label: "Dial gates",
      tone: (summary && summary.dialing_disabled_count ? "warn" : "ok") as PillTone,
      state: summary && summary.dialing_disabled_count ? `${summary.dialing_disabled_count} held` : "all clear",
      detail:
        summary && summary.dialing_disabled_count
          ? `${summary.dialing_disabled_count} org(s) with dialing administratively disabled`
          : "No orgs held by kill-switch",
    },
  ];

  return (
    <div style={{ padding: "28px 32px 48px", maxWidth: 1180 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        {statCards.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
        {!summary && <div style={{ gridColumn: "1 / -1", color: "var(--text-muted)", fontSize: 13 }}>Loading summary…</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 20, alignItems: "start" }}>
        {/* Dialing readiness (US1) */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 18, letterSpacing: "-0.015em", color: "var(--ink-900)" }}>Dialing readiness</h2>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--violet-500)", background: "var(--violet-50)", borderRadius: 999, padding: "4px 10px" }}>US1 · diagnostic</span>
          </div>
          <p style={{ margin: "0 0 18px", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
            Why can’t this org place calls? One read-only readout of every dial gate, evaluated live against the production dial path.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Organization</label>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={selectStyle}>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name || o.id}{o.is_personal ? " (personal)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>User <span style={{ fontWeight: 400, color: "var(--text-faint)" }}>(caller ID gate)</span></label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} style={selectStyle}>
                <option value="">— org-level only (no user) —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name || m.email || m.id}</option>
                ))}
              </select>
            </div>
          </div>

          {readyErr && <p style={{ color: "var(--danger-500)", fontSize: 13 }}>{readyErr}</p>}

          {verdict && (
            <>
              <div style={{ border: "1px solid var(--border-hairline)", borderRadius: 12, overflow: "hidden" }}>
                {verdict.gates.map((g, i) => (
                  <div key={g.key} style={{ display: "flex", alignItems: "stretch", borderTop: i === 0 ? "none" : "1px solid var(--border-hairline)", background: g.state === "fail" ? "rgba(190,18,60,0.03)" : "transparent" }}>
                    <div style={{ width: 4, alignSelf: "stretch", background: dotColor(gateTone(g.state)), flex: "none" }} />
                    <div style={{ flex: 1, minWidth: 0, padding: "13px 4px 13px 14px" }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>{g.label}</div>
                      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>{g.detail}</div>
                    </div>
                    <div style={{ padding: "0 14px", flex: "none", display: "flex", alignItems: "center" }}>
                      <span style={pillStyle(gateTone(g.state))}>{g.state === "pass" ? "PASS" : g.state === "fail" ? "FAIL" : "N/A"}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16, animation: "ws-cuein 0.28s var(--ease-lift)" }}>
                <div style={{ position: "relative", background: "var(--gradient-hud)", border: "1px solid var(--border-on-dark)", borderLeft: `3px solid ${canDial ? "var(--sky-400)" : "var(--violet-400)"}`, borderRadius: 12, padding: "18px 20px", boxShadow: "var(--elevation-hud)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                    <span style={{ fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: canDial ? "var(--sky-300)" : "var(--violet-200)" }}>Dialing readiness</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: "#fff", background: canDial ? "var(--success-500)" : "var(--danger-500)", borderRadius: 999, padding: "4px 12px" }}>{canDial ? "Can dial" : "Cannot dial"}</span>
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 13, lineHeight: 1.6, color: "var(--text-on-dark)" }}>
                    {canDial
                      ? `All dialing prerequisites met. This ${verdict.user_name ? verdict.user_name.split(" ")[0] : "org"} can place calls.`
                      : `Cannot dial — ${verdict.reasons.length ? verdict.reasons.join("; ") : "a prerequisite is not met"}.`}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 13, fontFamily: "var(--font-mono)", fontSize: 10.5, color: "#8A8398" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#7DDDA0" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--success-dot)" }} />live gate eval
                    </span>
                    <span>·</span>
                    <span>org: {verdict.org_name || verdict.org_id}{verdict.user_name ? ` · user: ${verdict.user_name}` : " · org-level"}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Platform health + coverage note */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 16, padding: "22px 24px", boxShadow: "var(--elevation-sm)" }}>
            <h2 style={{ margin: "0 0 16px", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 16, letterSpacing: "-0.01em", color: "var(--ink-900)" }}>Platform health</h2>
            {healthRows.map((h) => (
              <div key={h.label} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "13px 0", borderTop: "1px solid var(--border-hairline)" }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", marginTop: 5, flex: "none", background: dotColor(h.tone), boxShadow: `0 0 0 3px ${h.tone === "danger" ? "rgba(190,18,60,0.15)" : h.tone === "warn" ? "rgba(217,138,31,0.16)" : "rgba(59,209,122,0.18)"}` }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>{h.label}</span>
                    <Pill tone={h.tone}>{h.state}</Pill>
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.5 }}>{h.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: "linear-gradient(100deg,#FCFAFF,#fff)", border: "1px solid var(--border-hairline)", borderLeft: "3px solid var(--violet-500)", borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--violet-500)", marginBottom: 8 }}>Coverage note</div>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-body)", lineHeight: 1.55 }}>
              Payment-failure visibility (“did my card decline?”) is a known P0 gap — route those to the Stripe dashboard. Balance, tier and trial state are visible here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
